import {useQuery} from '@tanstack/react-query'
import type {AxiosResponse} from 'axios'
import isEqual from 'lodash.isequal'

import {EndpointsConfigTypes} from 'interfaces'
import {SettingsService} from 'services/api'
import {queryClient} from 'services/queryClient'
import {useEndpointConfigSnapshot} from 'hooks/snapshots/useEndpointConfigSnapshot'

export const ENDPOINTS_SETTINGS_QUERY_KEY = 'getEndpointsConfig'

export const defaultEndpointSettingsQueryResponse: EndpointsConfigTypes = {
  allowedDomains: [],
  antiTamperPassword: '',
  authProvider: '',
  endpointVersion: '',
  failClosed: false,
  installedEndpoints: 0,
  requireDelegatedAuth: false,
}

type EndpointSettingsQueryCache = Partial<AxiosResponse<EndpointsConfigTypes>>

export const useEndpointSettingsQuery = (
  isEnabled = true,
): {
  endpointsConfigData: EndpointsConfigTypes
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  refetch: () => void
} => {
  const {endpointConfigSnapshot, setEndpointConfigSnapshot} =
    useEndpointConfigSnapshot()

  const {
    data: endpointsConfigData,
    isLoading,
    isSuccess,
    isError,
    refetch,
  } = useQuery({
    queryKey: [ENDPOINTS_SETTINGS_QUERY_KEY],
    queryFn: () => SettingsService.getEndpointsConfig(),
    select: endpointsConfigResponse => endpointsConfigResponse.data,
    onSuccess: data => {
      if (!data?.endpointVersion) {
        return
      }
      if (!endpointConfigSnapshot || !isEqual(endpointConfigSnapshot, data)) {
        setEndpointConfigSnapshot(data)
      }
    },
    enabled: isEnabled,
    // A refetch replaces the cache, which would wipe an unsaved password when
    // the admin switches apps (for example to paste it into an MDM tool).
    // With no unsaved changes, refresh as usual so another tab's edit shows up.
    refetchOnWindowFocus: query =>
      !endpointConfigSnapshot ||
      isEqual(query.state.data?.data, endpointConfigSnapshot),
    refetchOnReconnect: query =>
      !endpointConfigSnapshot ||
      isEqual(query.state.data?.data, endpointConfigSnapshot),
  })

  return {
    endpointsConfigData:
      endpointsConfigData ?? defaultEndpointSettingsQueryResponse,
    isLoading,
    isSuccess,
    isError,
    refetch: () => void refetch(),
  }
}

export const getEndpointSettingsQueryCache = (): EndpointSettingsQueryCache => {
  const queryData = queryClient.getQueryData<EndpointSettingsQueryCache>([
    ENDPOINTS_SETTINGS_QUERY_KEY,
  ])
  return queryData ?? {}
}

export const setEndpointsSettingCache = (
  payload: EndpointsConfigTypes,
): void => {
  // A fetch still in flight (for example a refocus refetch that started just
  // before this edit) would land after it and overwrite the draft and the
  // saved snapshot. Cancel it first; the write waits for the cancel to settle
  // so the cancel's own restore of the previous data can't undo it.
  void queryClient
    .cancelQueries({queryKey: [ENDPOINTS_SETTINGS_QUERY_KEY]})
    .then(() => {
      const endpointsSettingConfig = getEndpointSettingsQueryCache()
      queryClient.setQueryData([ENDPOINTS_SETTINGS_QUERY_KEY], {
        ...endpointsSettingConfig,
        data: payload,
      })
    })
}
